import React, { useMemo } from 'react'
import { generateImageUrl } from 'Utils/generateImageUrl'
import { ProductionUnit, ThProductionUnit } from 'Configuration/ProductionUnit'
import { ProductionMeasurement, ThProductionMeasurement } from 'Configuration/ProductionMeasurement'
import FormGenerator from 'Presentation/Components/Form/FormGenerator'
import { useLang } from '_metronic/i18n/Metronici18n'

interface Props {
	nameTh: string
	nameEn: string
	featurePicturePath: string
	code: string
	quantity: number
	shortDescription: string

	productLine?: {
		nameEn: string
		nameTh: string
		featurePicturePath: string | null
		parent?: {
			nameEn: string
			nameTh: string
			featurePicturePath: string | null
		}
	}

	dimension?: {
		width: number
		length: number
		height: number
		weightGram: number
		verticalStack: number
		unit: ProductionUnit | ThProductionUnit
		measurementApproach: string
	}
	options?: {
		taxonomyProductLineOption: {
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}[]
	accessories?: {
		taxonomyProductLineAccessory: {
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}[]
	thickness?: {
		nameTh: string
		nameEn: string
		featurePicturePath: string
	}
	outerMaterial?: {
		nameTh: string
		nameEn: string
		featurePicturePath: string
	}
	innerMaterial?: {
		nameTh: string
		nameEn: string
		featurePicturePath: string
	}
	printingOption?: {
		nameTh: string
		nameEn: string
		featurePicturePath: string
	}
	forming?: {
		nameTh: string
		nameEn: string
		featurePicturePath: string
	}
	sustainabilities?: {
		oemRequestItemId: number
		sustainabilityId: number
		taxonomySustainability: {
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}[]
	certificates?: {
		oemRequestItemId: number
		certificateId: number
		taxonomyCertificates: {
			nameTh: string
			nameEn: string
			featurePicturePath: string
		}
	}[]

	showInquiry?: boolean
	inquiries?: {
		uuId: string
		code: string
		status: string
		statusTitleEn: string
		statusTitleTh: string
		canDownload: boolean
		supplier: {
			uuId: string
			displayTitle: string
			companyName: string
			companyLogo: string
		}
	}[]

	disabledNote?: boolean
	note?: string
	onChangeNote?: (value: string) => void
}

const ProductCellWithoutSustainCerti: React.FC<Props> = ({
	nameEn,
	nameTh,
	featurePicturePath,
	productLine,

	dimension,
	options = [],
	accessories = [],
	thickness,
	outerMaterial,
	innerMaterial,
	printingOption,
	forming,

	disabledNote,
	note,
	onChangeNote,
}) => {
	const randomNumber = Math.floor(Math.random() * 1000)
	const lang = useLang()

	const showDetailText = useMemo(() => {
		if (lang === 'en') {
			return 'View/Hide Detail'
		} else {
			return 'ดู/ซ่อนรายละเอียด'
		}
	}, [lang])

	return (
		<div>
			{/* begin: Product Information */}
			<div className="row gx-10">
				<div className="d-none d-xl-flex col-12 col-xl-2 px-0">
					<img
						className="ms-4"
						src={
							featurePicturePath
								? generateImageUrl(featurePicturePath)
								: '/media/icons/kumopack/default-placeholder.png'
						}
						onError={(e: any) => {
							e.target.onerror = null
							e.target.src = '/media/icons/kumopack/default-placeholder.png'
						}}
						alt={nameEn}
						height={78}
						style={{ userSelect: 'none', pointerEvents: 'none' }}
					/>
				</div>

				<div className="col-12 col-xl-10 d-flex flex-column" style={{ rowGap: '5px' }}>
					<div className="fw-bold text-kumopack-grey-900 fs-4">
						{nameTh} - {nameEn}
					</div>
					<div className="text-kumopack-grey-600">
						<div className="row py-5 gy-2">
							{productLine?.parent && (
								<div className="col-12">
									<div className="text-kumopack-grey-900 fs-4 fw-bold">
										กลุ่มผลิตภัณฑ์:{' '}
										{lang === 'en' ? productLine?.parent?.nameEn : productLine?.parent?.nameTh}
									</div>
								</div>
							)}

							<div className="col-12">
								<div className="text-kumopack-grey-900 fs-5">
									ประเภทสินค้า: {lang === 'en' ? productLine?.nameEn : productLine?.nameTh}
								</div>
							</div>

							{dimension && (
								<div className="accordion accordion-icon-toggle" id="product-cell-accordion">
									<div className="mb-5">
										<div
											className="accordion-header py-3 d-flex"
											data-bs-toggle="collapse"
											data-bs-target={`#product-cell-${randomNumber}`}>
											<button className="btn btn-sm btn-kumopack-primary-50 text-kumopack-primary-700 fw-bold">
												{showDetailText}
											</button>
										</div>
										<div
											id={`product-cell-${randomNumber}`}
											className={'fs-6 show'}
											data-bs-parent="#product-cell-accordion">
											<div className="row gy-2">
												<React.Fragment>
													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ประเภทส่วนเสริม:{' '}
															{options
																.map(option => option.taxonomyProductLineOption?.nameTh)
																.join(', ')}
															{options.length === 0 && '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ประเภทส่วนเสริม Accessory:{' '}
															{accessories
																.map(accessory => accessory.taxonomyProductLineAccessory?.nameTh)
																.join(', ')}
															{accessories.length === 0 && '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ลักษณะการวัด:{' '}
															{dimension?.measurementApproach
																? lang === 'en'
																	? // @ts-ignore
																	  ProductionMeasurement[dimension?.measurementApproach]
																	: // @ts-ignore
																	  ThProductionMeasurement[dimension.measurementApproach]
																: '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ขนาดผลิตภัณฑ์: กว้าง {dimension?.width}{' '}
															{lang === 'en'
																? // @ts-ignore
																  ProductionUnit[dimension?.unit]
																: // @ts-ignore
																  ThProductionUnit[dimension.unit]}{' '}
															x ยาว {dimension?.length}{' '}
															{lang === 'en'
																? // @ts-ignore
																  ProductionUnit[dimension?.unit]
																: // @ts-ignore
																  ThProductionUnit[dimension.unit]}{' '}
															x สูง {dimension?.height}{' '}
															{lang === 'en'
																? // @ts-ignore
																  ProductionUnit[dimension?.unit]
																: // @ts-ignore
																  ThProductionUnit[dimension.unit]}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ความหนา: {thickness?.nameTh ?? '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															วัสดุภายนอก: {outerMaterial?.nameTh ?? '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															วัสดุภายใน: {innerMaterial?.nameTh ?? '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															น้ำหนักสินค้า:{' '}
															{dimension?.weightGram ? `${dimension?.weightGram} กรัม` : '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															จำนวนการเรียงซ้อน:{' '}
															{dimension?.verticalStack ? `${dimension?.verticalStack} ชิ้น` : '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ประเภทการพิมพ์: {printingOption?.nameTh ?? '-'}
														</div>
													</div>

													<div className="col-12">
														<div className="text-kumopack-grey-500 fs-6">
															ลักษณะการขึ้นรูป: {forming?.nameTh ?? '-'}
														</div>
													</div>
												</React.Fragment>
											</div>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* begin: Note */}
					{note !== undefined && (
						<div className="row">
							<div className="col-12 mb-3">
								<img
									src="/media/icons/kumopack/red-alert.svg"
									alt="Red Alert Icon"
									style={{ userSelect: 'none', pointerEvents: 'none' }}
									className="me-2"
								/>{' '}
								ข้อความเพิ่มเติมจากผู้ผลิต
							</div>

							<div className="col-12">
								<FormGenerator
									formKey="note"
									inputType="textarea"
									value={note}
									onChange={e => {
										if (onChangeNote) {
											onChangeNote(e.target.value)
										}
									}}
									disabled={disabledNote}
								/>
							</div>
						</div>
					)}
					{/* end: Note */}
				</div>
			</div>
			{/* end: Product Information */}
		</div>
	)
}

export default ProductCellWithoutSustainCerti
